import React from 'react';
import { motion } from 'framer-motion';
import { PortfolioFolder } from '../../types';
import { FolderIcon, PlusIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import SmartImage from '../common/SmartImage';
import { Menu } from '@headlessui/react';
import Button from '../common/Button';

interface PortfolioFolderGridProps {
  folders: PortfolioFolder[];
  isOwner: boolean;
  onCreateClick?: () => void;
  onFolderClick: (folder: PortfolioFolder) => void;
  onEditFolder?: (folder: PortfolioFolder) => void;
  onDeleteFolder?: (folder: PortfolioFolder) => void;
}

const PortfolioFolderGrid: React.FC<PortfolioFolderGridProps> = ({
  folders,
  isOwner,
  onCreateClick,
  onFolderClick,
  onEditFolder,
  onDeleteFolder
}) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mt-6">
      {isOwner && (
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <button
            onClick={onCreateClick}
            className="w-full h-full min-h-[160px] md:min-h-[220px] rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center justify-center hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all group"
          >
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-3 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/40 group-hover:text-primary-600 transition-colors">
              <PlusIcon className="h-6 w-6 text-gray-400 group-hover:text-primary-600" />
            </div>
            <span className="font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-600">
              Create Folder
            </span>
          </button>
        </motion.div>
      )}

      {!isOwner && folders.length === 0 && (
        <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
          <FolderIcon className="h-12 w-12 mb-2 text-gray-300 dark:text-gray-600" />
          <span className="text-sm">No folders created yet</span>
        </div>
      )}

      {folders.map((folder, index) => (
        <motion.div
          key={folder.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 hover:shadow-lg hover:border-gray-200 dark:hover:border-gray-600 transition-all group flex flex-col"
        >
          {/* Card Image Grid */}
          <div 
            className="aspect-[4/3] bg-gray-100 dark:bg-gray-900 grid grid-cols-2 grid-rows-2 gap-0.5 relative rounded-t-2xl overflow-hidden cursor-pointer"
            onClick={() => onFolderClick(folder)}
          >
            {folder.preview_urls && folder.preview_urls.length > 0 ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-gray-200 dark:bg-gray-800 overflow-hidden relative">
                  {folder.preview_urls[i] && (
                    <SmartImage
                      src={folder.preview_urls[i]}
                      alt={`${folder.name} preview ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  )}
                </div>
              ))
            ) : (
              <div className="col-span-2 row-span-2 flex flex-col items-center justify-center text-gray-400">
                <FolderIcon className="h-12 w-12 mb-2 text-gray-300 dark:text-gray-600" />
                <span className="text-sm">Empty Folder</span>
              </div>
            )}
            
            {/* Folder Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4">
              <div className="flex items-center space-x-2 text-white">
                <FolderIcon className="h-5 w-5 fill-white/20" />
              </div>
            </div>
          </div>

          <div className="p-3 md:p-4 flex items-center justify-between">
            <div 
              className="flex flex-col flex-1 cursor-pointer overflow-hidden"
              onClick={() => onFolderClick(folder)}
            >
              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {folder.name}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {folder.item_count} {folder.item_count === 1 ? 'item' : 'items'}
              </span>
            </div>

            {isOwner && (
              <Menu as="div" className="relative ml-2">
                <Menu.Button className="p-1.5 md:p-2 -mr-1 md:-mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <span className="sr-only">Open options</span>
                  <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
                </Menu.Button>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <Menu.Items className="absolute right-0 bottom-full mb-2 w-36 origin-bottom-right rounded-xl bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-100 dark:border-gray-700 z-[60] overflow-hidden">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => onEditFolder?.(folder)}
                            className={`${
                              active ? 'bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                            } flex w-full items-center px-4 py-2 text-sm transition-colors`}
                          >
                            Edit Folder
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => onDeleteFolder?.(folder)}
                            className={`${
                              active ? 'bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-400' : 'text-red-600 dark:text-red-400'
                            } flex w-full items-center px-4 py-2 text-sm transition-colors`}
                          >
                            Delete Folder
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </motion.div>
              </Menu>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default PortfolioFolderGrid;
